/**
 * Physics-based spring easing, modeled after anime.js `spring()`
 * (https://animejs.com/documentation/easings/spring): a damped harmonic
 * oscillator whose settling time DEFINES the animation duration — the
 * configured duration is ignored while a spring is active.
 */

export type SpringParams = {
  /** 1–10000; higher = more inertia (slower, heavier motion) */
  mass: number;
  /** 1–10000; higher = tighter spring, faster response */
  stiffness: number;
  /** 0–10000; higher = less bounce */
  damping: number;
  /** -10000–10000; initial momentum ("running start") */
  velocity: number;
};

export const SPRING_DEFAULTS: SpringParams = {
  mass: 1,
  stiffness: 100,
  damping: 10,
  velocity: 0,
};

export const SPRING_RANGES: Record<
  keyof SpringParams,
  { min: number; max: number; step: number }
> = {
  mass: { min: 1, max: 10000, step: 1 },
  stiffness: { min: 1, max: 10000, step: 1 },
  damping: { min: 0, max: 10000, step: 1 },
  velocity: { min: -10000, max: 10000, step: 1 },
};

export const isSpringParams = (value: unknown): value is SpringParams => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (["mass", "stiffness", "damping", "velocity"] as const).every(
    (key) => {
      const val = v[key];
      if (typeof val !== "number" || !Number.isFinite(val)) {
        return false;
      }
      // enforce SPRING_RANGES bounds during validation
      const range = SPRING_RANGES[key];
      if (val < range.min || val > range.max) {
        console.warn(
          `[animation] spring param "${key}" = ${val} outside bounds [${range.min}, ${range.max}]`,
        );
        return false;
      }
      return true;
    },
  );
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const SETTLE_THRESHOLD = 0.001;
const MAX_DURATION_MS = 10_000;
const MIN_DURATION_MS = 100;

/** Closed-form solution of a unit spring from 0 → 1. Returns the eased
 * progress function (normalized to the settling duration, GSAP-compatible)
 * and the settling duration itself. */
export const createSpring = (params: SpringParams) => {
  const m = clamp(params.mass, SPRING_RANGES.mass.min, SPRING_RANGES.mass.max);
  const k = clamp(
    params.stiffness,
    SPRING_RANGES.stiffness.min,
    SPRING_RANGES.stiffness.max,
  );
  const c = clamp(
    params.damping,
    SPRING_RANGES.damping.min,
    SPRING_RANGES.damping.max,
  );
  const v0 = clamp(
    params.velocity,
    SPRING_RANGES.velocity.min,
    SPRING_RANGES.velocity.max,
  );

  const w0 = Math.sqrt(k / m); // undamped angular frequency (rad/s)
  const zeta = c / (2 * Math.sqrt(k * m)); // damping ratio

  let progressAt: (tSec: number) => number;
  if (zeta < 1) {
    // underdamped — oscillates while settling
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    const b = (zeta * w0 - v0) / wd;
    progressAt = (t) =>
      1 - Math.exp(-zeta * w0 * t) * (Math.cos(wd * t) + b * Math.sin(wd * t));
  } else {
    // critically damped / overdamped — approaches 1 without oscillating
    // (overdamped approximated with the critical solution; visually
    // equivalent at these parameter ranges)
    progressAt = (t) => 1 - (1 + (w0 - v0) * t) * Math.exp(-w0 * t);
  }

  // settling time: last millisecond the spring is still visibly moving
  let durationMs = MIN_DURATION_MS;
  let found = false;
  for (let t = MAX_DURATION_MS; t >= MIN_DURATION_MS; t -= 1) {
    if (Math.abs(1 - progressAt(t / 1000)) >= SETTLE_THRESHOLD) {
      durationMs = Math.min(t + 1, MAX_DURATION_MS);
      found = true;
      break;
    }
  }
  if (
    !found &&
    Math.abs(1 - progressAt(MAX_DURATION_MS / 1000)) < SETTLE_THRESHOLD
  ) {
    console.warn(
      `[animation] spring params may not settle; capping to ${MAX_DURATION_MS}ms. Consider reducing mass or increasing stiffness.`,
    );
  }

  const durationSec = durationMs / 1000;
  const ease = (p: number) => (p >= 1 ? 1 : progressAt(p * durationSec));
  return { ease, durationMs };
};
